import { type ReactNode, useEffect, useRef } from "react";

interface ScrollFilmProps {
	/** URL of frame i. */
	frameUrl: (index: number) => string;
	frameCount: number;
	/** Chapters, each a block in normal flow scrolling over the pinned film. */
	children: ReactNode;
}

/**
 * A film scrubbed by scroll: the stage is pinned while the chapters scroll
 * past, and the scroll position through the whole block picks the frame.
 *
 * The server renders the first frame as a plain <img>, so the prerendered
 * page, a no-JS visit and a reduced-motion visit all show a finished still.
 * The canvas takes over only once the frame it needs has loaded.
 */
export function ScrollFilm({
	frameUrl,
	frameCount,
	children,
}: ScrollFilmProps) {
	const rootRef = useRef<HTMLDivElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);

	useEffect(() => {
		const root = rootRef.current;
		const canvas = canvasRef.current;
		if (!root || !canvas) return;
		if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
		let ctx: CanvasRenderingContext2D | null = null;

		const dpr = Math.min(window.devicePixelRatio || 1, 2);
		const frames: HTMLImageElement[] = [];
		const loaded: boolean[] = [];
		let wanted = 0;
		let drawn = -1;
		let raf = 0;

		const nearestLoaded = (i: number) => {
			for (let d = 0; d < frameCount; d++) {
				if (loaded[i - d]) return i - d;
				if (loaded[i + d]) return i + d;
			}
			return -1;
		};

		const draw = () => {
			raf = 0;
			const k = nearestLoaded(wanted);
			if (k < 0) return;
			// Asked for only once a frame exists to paint.
			ctx ??= canvas.getContext("2d");
			if (!ctx) return;
			const w = Math.round(canvas.clientWidth * dpr);
			const h = Math.round(canvas.clientHeight * dpr);
			if (!w || !h) return;
			const resized = canvas.width !== w || canvas.height !== h;
			if (resized) {
				canvas.width = w;
				canvas.height = h;
			}
			if (k === drawn && !resized) return;
			const img = frames[k];
			// Cover-fit, anchored low so the sauna stays in frame on tall screens.
			const s = Math.max(w / img.naturalWidth, h / img.naturalHeight);
			const dw = img.naturalWidth * s;
			const dh = img.naturalHeight * s;
			ctx.drawImage(img, (w - dw) / 2, (h - dh) * 0.6, dw, dh);
			drawn = k;
			root.dataset.film = "live";
		};
		const schedule = () => {
			if (!raf) raf = requestAnimationFrame(draw);
		};

		const onScroll = () => {
			const r = root.getBoundingClientRect();
			const span = root.offsetHeight - window.innerHeight;
			const t = span > 0 ? Math.min(1, Math.max(0, -r.top / span)) : 0;
			wanted = Math.round(t * (frameCount - 1));
			schedule();
		};

		for (let i = 0; i < frameCount; i++) {
			const img = new Image();
			img.decoding = "async";
			img.onload = () => {
				loaded[i] = true;
				// A closer frame than the one on screen just arrived.
				if (Math.abs(i - wanted) < Math.abs(drawn - wanted)) schedule();
				if (drawn < 0) schedule();
			};
			img.src = frameUrl(i);
			frames.push(img);
		}

		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		window.addEventListener("resize", schedule);
		return () => {
			window.removeEventListener("scroll", onScroll);
			window.removeEventListener("resize", schedule);
			if (raf) cancelAnimationFrame(raf);
			for (const img of frames) img.onload = null;
		};
	}, [frameCount, frameUrl]);

	return (
		<div className="sky-film" ref={rootRef}>
			<div className="sky-stage" aria-hidden="true">
				<img
					className="sky-poster"
					src={frameUrl(0)}
					alt=""
					fetchPriority="high"
				/>
				<canvas className="sky-canvas" ref={canvasRef} />
			</div>
			<div className="sky-chapters">{children}</div>
		</div>
	);
}
